import { RAID_BRIDGE_USERSCRIPT_VERSION } from '../api/raidBridgeInstaller.js';

const TAMPERMONKEY_DOWNLOAD_URL = 'https://www.tampermonkey.net/index.php?locale=ko';

function StepStatus({ done, current, outdated = false, updateRequired = false }) {
  if (updateRequired) return <em className="outdated">업데이트 필요</em>;
  if (outdated) return <em className="outdated">구형 토큰</em>;
  return <em className={done ? 'done' : current ? 'current' : 'waiting'}>{done ? '완료' : current ? '진행 필요' : '대기'}</em>;
}

export function RaidBridgeQuickStart({ bridge, mobile = false }) {
  const installed = Boolean(bridge.installer.installed);
  const installedVersion = String(bridge.installer.version || '').trim();
  const latestVersion = String(bridge.latestInstallerVersion || RAID_BRIDGE_USERSCRIPT_VERSION).trim();
  const updateRequired = Boolean(bridge.installerUpdateRequired);
  const multipleVersions = Boolean(bridge.installer.multipleVersions);
  const detectedVersions = Array.isArray(bridge.installer.detectedVersions)
    ? bridge.installer.detectedVersions.filter(Boolean)
    : [];
  const selectedSeasonNo = Number(bridge.selectedSeasonNo || 0);
  const hasSeason = Array.isArray(bridge.seasons) && bridge.seasons.length > 0;
  const sharedTokenReady = Boolean(bridge.sharedTokenReady);
  const connected = Boolean(bridge.configuredForSeason);
  const ready = Boolean(bridge.collectorReady);
  const currentStep = !hasSeason ? 1 : !installed || updateRequired ? 2 : !connected ? 3 : 4;
  const applying = bridge.working === 'apply-shared' || bridge.working === 'season';

  const rotateSharedToken = () => {
    if (bridge.activeToken && !window.confirm(
      '공용 토큰을 재발급하면 모든 관리자 PC에 저장된 기존 토큰이 즉시 구형 토큰이 됩니다. 계속할까요?',
    )) return;
    bridge.rotate();
  };

  const scrollToCollector = () => document.getElementById('raid-bridge-direct-sync')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const nextTask = currentStep === 1
    ? { text: '진행 중인 공용 시즌이 없습니다. 플랫폼 관리자에게 시즌 공개를 요청해주세요.', label: '공용 시즌 확인', href: '#/management/season' }
    : currentStep === 2
      ? {
        text: !installed
          ? `최신 수집기 v${latestVersion}를 설치하세요.`
          : `설치된 v${installedVersion || '확인 불가'}를 최신 v${latestVersion}로 업데이트하세요.`,
        label: installed ? '최신 수집기 업데이트' : '수집기 설치',
        onClick: bridge.openInstaller,
      }
      : currentStep === 3 && !sharedTokenReady
        ? {
          text: bridge.activeToken
            ? '기존 토큰은 화면에서 공유할 수 없습니다. 공용 토큰으로 한 번 재발급해주세요.'
            : '유니온 공용 토큰을 먼저 발급해주세요.',
          label: bridge.working === 'rotate' ? '발급 중…' : '공용 토큰 발급',
          onClick: rotateSharedToken,
          disabled: Boolean(bridge.working),
        }
        : currentStep === 3
          ? {
            text: bridge.tokenOutdated
              ? '구형 토큰입니다. 새 공용 토큰을 이 PC 수집기에 저장해주세요.'
              : `유니온 공용 토큰과 ${selectedSeasonNo || '-'}차를 이 PC 수집기에 저장해주세요.`,
            label: applying ? '저장 중…' : '공용 토큰 저장',
            onClick: bridge.applySharedToken,
            disabled: !installed || updateRequired || !sharedTokenReady || !selectedSeasonNo || Boolean(bridge.working),
          }
          : { text: '같은 브라우저에서 BlablaLink에 로그인한 뒤 수집을 실행하세요.', label: '수집 영역으로 이동', onClick: scrollToCollector };

  return (
    <section className={`raid-bridge-setup ${mobile ? 'mobile' : 'desktop'}`} aria-label="레이드 수집기 처음 설정">
      <header className="raid-bridge-section-header">
        <div>
          <h3>처음 설정</h3>
          <p>처음 한 번만 아래 순서대로 설정하면 이후에는 수집 버튼만 사용합니다.</p>
        </div>
        <strong className={ready ? 'ready' : 'setup'}>{ready ? '설정 완료' : `${currentStep}단계 진행`}</strong>
      </header>

      <p className="raid-bridge-browser-note">
        PC Chrome 또는 Edge에서 DoroParty와 BlablaLink를 같은 브라우저로 열어주세요. DoroParty는 BlablaLink 비밀번호를 입력받거나 저장하지 않습니다.
      </p>

      <div className="raid-bridge-current-task" aria-live="polite">
        <div><span>지금 할 일</span><strong>{nextTask.text}</strong></div>
        {nextTask.href
          ? <a href={nextTask.href}>{nextTask.label}</a>
          : <button type="button" disabled={Boolean(nextTask.disabled)} onClick={nextTask.onClick}>{nextTask.label}</button>}
      </div>

      <ol className="raid-bridge-setup-list">
        <li className={hasSeason ? 'done' : currentStep === 1 ? 'current' : ''}>
          <b>1</b>
          <div className="raid-bridge-step-copy">
            <div><strong>진행 중인 공용 시즌 확인</strong><StepStatus done={hasSeason} current={currentStep === 1} /></div>
            <p>플랫폼 관리자가 공개한 진행 중인 시즌에만 실전 기록을 저장할 수 있습니다.</p>
          </div>
          <a className="raid-bridge-step-action" href="#/management/season">공용 시즌 확인</a>
        </li>

        <li className={installed && !updateRequired ? 'done' : currentStep === 2 ? 'current' : ''}>
          <b>2</b>
          <div className="raid-bridge-step-copy">
            <div>
              <strong>Tampermonkey와 수집기 설치</strong>
              <StepStatus done={installed && !updateRequired} current={currentStep === 2} updateRequired={updateRequired} />
            </div>
            <p>BlablaLink 화면의 레이드 기록을 읽기 위해 필요한 브라우저 확장과 수집 스크립트입니다.</p>
            <small>
              최신 버전 v{latestVersion} · 설치 버전 {installedVersion ? `v${installedVersion}` : '감지되지 않음'}
            </small>
            {updateRequired && installed ? (
              <small className="raid-bridge-version-warning" role="alert">
                업데이트 후에도 구형 버전으로 표시되면 수집기가 중복 설치된 상태입니다. Tampermonkey에서 이전에 설치한 유니온 레이드 수집기를 모두 삭제한 뒤 최신 버전 하나만 다시 설치해주세요.
              </small>
            ) : null}
            {multipleVersions ? (
              <small className="raid-bridge-version-warning" role="alert">
                여러 버전이 동시에 감지됐습니다: {detectedVersions.map((version) => `v${version}`).join(', ')}. Tampermonkey에서 관련 수집기를 모두 삭제한 뒤 최신 버전 하나만 다시 설치해주세요.
              </small>
            ) : null}
            {!installed ? (
              <details className="raid-bridge-install-help">
                <summary>Tampermonkey 설치 방법</summary>
                <ol>
                  <li>공식 페이지에서 Tampermonkey를 설치합니다.</li>
                  <li>확장 관리에서 ‘사용자 스크립트 허용’을 켭니다. 항목이 없으면 개발자 모드를 먼저 켭니다.</li>
                  <li>DoroParty에서 ‘수집기 설치’를 누르고 설치가 끝나면 ‘설치 확인’을 누릅니다.</li>
                </ol>
                <a href={TAMPERMONKEY_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">Tampermonkey 공식 페이지</a>
              </details>
            ) : null}
          </div>
          <div className="raid-bridge-step-actions">
            <button type="button" onClick={bridge.openInstaller}>{installed ? '최신 수집기 업데이트' : '수집기 설치'}</button>
            <button type="button" disabled={bridge.installer.checking} onClick={bridge.probeInstaller}>{bridge.installer.checking ? '확인 중…' : '버전 다시 확인'}</button>
          </div>
        </li>

        <li className={`raid-bridge-connection-step ${connected ? 'done' : currentStep === 3 ? 'current' : ''}`}>
          <b>3</b>
          <div className="raid-bridge-step-copy">
            <div>
              <strong>유니온 공용 토큰 저장</strong>
              <StepStatus done={connected} current={currentStep === 3} outdated={bridge.tokenOutdated} />
            </div>
            <p>같은 유니온의 모든 관리자는 이 화면에서 같은 공용 토큰을 자신의 PC 수집기에 저장합니다. 토큰을 서로 복사할 필요가 없습니다.</p>
            <small>{!sharedTokenReady
              ? bridge.activeToken
                ? '기존 해시 전용 토큰은 공유할 수 없어 공용 토큰 재발급이 필요합니다.'
                : '아직 발급된 공용 토큰이 없습니다.'
              : bridge.tokenOutdated
                ? '구형 토큰입니다. 새 공용 토큰을 저장해주세요.'
                : connected
                  ? `${selectedSeasonNo}차 · 이 PC에 최신 공용 토큰 저장 완료`
                  : '공용 토큰 저장 버튼을 누르면 현재 PC의 Tampermonkey에 자동 저장됩니다.'}</small>
          </div>
          <div className="raid-bridge-step-actions raid-bridge-shared-token-actions">
            {sharedTokenReady ? (
              <button
                type="button"
                disabled={!installed || updateRequired || !selectedSeasonNo || Boolean(bridge.working)}
                onClick={bridge.applySharedToken}
              >
                {applying ? '저장 중…' : bridge.tokenOutdated ? '새 공용 토큰 저장' : '공용 토큰 저장'}
              </button>
            ) : (
              <button type="button" disabled={Boolean(bridge.working)} onClick={rotateSharedToken}>
                {bridge.working === 'rotate' ? '발급 중…' : '공용 토큰 발급'}
              </button>
            )}
            {sharedTokenReady ? (
              <button type="button" className="danger" disabled={Boolean(bridge.working)} onClick={rotateSharedToken}>
                {bridge.working === 'rotate' ? '재발급 중…' : '공용 토큰 재발급'}
              </button>
            ) : null}
          </div>
          {bridge.tokenOutdated ? (
            <p className="raid-bridge-outdated-token-warning" role="alert">
              구형 토큰입니다. 새 공용 토큰을 저장해주세요. 저장 전에는 실전 수집과 자동 수집이 실행되지 않습니다.
            </p>
          ) : null}
        </li>

        <li className={ready ? 'done current' : ''}>
          <b>4</b>
          <div className="raid-bridge-step-copy">
            <div><strong>BlablaLink 로그인</strong><StepStatus done={ready} current={currentStep === 4} /></div>
            <p>같은 브라우저의 새 탭에서 BlablaLink에 로그인한 뒤 DoroParty로 돌아오세요.</p>
          </div>
          <button type="button" className="raid-bridge-step-action" disabled={!ready} onClick={scrollToCollector}>수집 영역으로 이동</button>
        </li>
      </ol>
    </section>
  );
}
